import React from 'react';
import { Pressable, StyleSheet, View } from 'react-native';

import { Text, tokens } from '@fhs/ui';

enum ServiceMode {
  DRIVE_THRU = 'DRIVE_THRU',
  EAT_IN = 'EAT_IN',
  TAKEOUT = 'TAKEOUT',
  CURBSIDE = 'CURBSIDE',
  PICKUP_WINDOW = 'PICKUP_WINDOW',
  DELIVERY = 'DELIVERY',
}

export interface ServiceModeConfig {
  serviceMode: ServiceMode;
  header: string;
  description: string;
  unavailable: boolean;
  hidden?: boolean;
}

export interface PickupServiceModeOptionProps {
  config: ServiceModeConfig;
  isSelected: boolean;
  index: number;
  onSelect: () => void;
}

export function PickupServiceModeOption({
  config,
  isSelected,
  index,
  onSelect,
}: PickupServiceModeOptionProps) {
  const { serviceMode, header, description, unavailable, hidden = false } = config;

  if (hidden) {
    return null;
  }

  const headerText = unavailable ? `${header} (Unavailable)` : header;

  return (
    <Pressable
      disabled={unavailable}
      testID={`service-mode-button-${serviceMode}`}
      onPress={onSelect}
      style={[
        styles.container,
        { paddingTop: index > 0 ? 0 : 12 },
        unavailable && styles.disabledContainer,
      ]}
      accessibilityLabel={`cart-service-mode-selection-${headerText}`}
      accessibilityRole="radio"
      accessibilityState={{ checked: isSelected }}
      accessibilityHint={description}
    >
      {index > 0 && <View style={styles.borderBottom} />}

      <View style={styles.content}>
        <View style={[styles.radioOuter, isSelected && styles.radioOuterSelected]}>
          {isSelected && <View style={styles.radioInner} />}
        </View>

        <View style={styles.textContainer}>
          <Text.Ui size="md" weight="bold">
            {headerText}
          </Text.Ui>
          <Text.Ui size="sm">{description}</Text.Ui>
        </View>
      </View>
    </Pressable>
  );
}

const styles = StyleSheet.create({
  container: {
    width: '100%',
    paddingVertical: 12,
    paddingHorizontal: 16,
  },
  disabledContainer: {
    opacity: 0.5,
  },
  content: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  textContainer: {
    flex: 1,
    gap: 4,
  },
  chevronContainer: {
    justifyContent: 'center',
    alignItems: 'center',
  },
  radioOuter: {
    width: 20,
    height: 20,
    borderRadius: 3,
    borderWidth: 2,
    borderColor: tokens.colors.$blackOpacity10,
    justifyContent: 'center',
    alignItems: 'center',
  },
  radioOuterSelected: {
    borderColor: tokens.colors.$blackOpacity55,
  },
  radioInner: {
    width: 10,
    height: 10,
    borderRadius: 5,
    backgroundColor: tokens.colors.$black,
  },
  borderBottom: {
    width: '60%',
    height: StyleSheet.hairlineWidth,
    backgroundColor: tokens.colors.$blackOpacity10,
    alignSelf: 'flex-start',
    marginBottom: 16,
  },
});
